import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import './CalendarModal.css';

export default function JoinCalendarModal({ onClose, onSuccess }) {
  const { currentUser } = useAuth();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleInputChange(e) {
    // Convert to uppercase and only allow letters/numbers
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 6) {
      setInviteCode(value);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!inviteCode || inviteCode.length !== 6) {
      return setError('يرجى إدخال رمز دعوة صحيح مكون من 6 أحرف');
    }

    if (!currentUser) {
      return setError('يجب تسجيل الدخول أولاً للانضمام للمجموعة');
    }

    try {
      setLoading(true);
      setError('');

      // Search for calendar with this invite code
      const calendarsRef = collection(db, 'calendars');
      const q = query(calendarsRef, where('inviteCode', '==', inviteCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('رمز الدعوة غير صحيح. يرجى التحقق من الرمز والمحاولة مرة أخرى.');
        setLoading(false);
        return;
      }

      const calendarDoc = querySnapshot.docs[0];
      const calendarData = calendarDoc.data();
      const calendarId = calendarDoc.id;

      // Check if user is already a member
      if (calendarData.members && calendarData.members.includes(currentUser.uid)) {
        setError('أنت بالفعل عضو في هذه المجموعة!');
        setLoading(false);
        return;
      }

      // Add user to calendar members
      await updateDoc(doc(db, 'calendars', calendarId), {
        members: arrayUnion(currentUser.uid)
      });

      // Add calendar to user's calendars array
      await updateDoc(doc(db, 'users', currentUser.uid), {
        calendars: arrayUnion(calendarId)
      });

      // Success!
      onSuccess({
        id: calendarId,
        name: calendarData.name,
        inviteCode: inviteCode
      });

    } catch (err) {
      console.error('Error joining calendar:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);

      // Show more specific error messages
      if (err.code === 'permission-denied') {
        setError('ليس لديك صلاحية للانضمام. تأكد من تسجيل الدخول.');
      } else if (err.code === 'not-found') {
        setError('لم يتم العثور على المجموعة.');
      } else if (err.code === 'unavailable') {
        setError('الخدمة غير متاحة حالياً. حاول مرة أخرى.');
      } else {
        setError('حدث خطأ أثناء الانضمام للمجموعة. يرجى المحاولة مرة أخرى.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content calendar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>الانضمام لمجموعة</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <p className="modal-description">
          أدخل رمز الدعوة المكون من 6 أحرف للانضمام إلى المجموعة.
        </p>

        {error && <div className="error-alert">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="inviteCode">رمز الدعوة *</label>
            <input
              id="inviteCode"
              type="text"
              value={inviteCode}
              onChange={handleInputChange}
              placeholder="مثال: ABC123"
              disabled={loading}
              maxLength={6}
              style={{
                fontSize: '24px',
                letterSpacing: '4px',
                textAlign: 'center',
                fontFamily: 'monospace',
                textTransform: 'uppercase'
              }}
            />
            <span className="input-hint">{inviteCode.length}/6</span>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || inviteCode.length !== 6}
            >
              {loading ? 'جاري الانضمام...' : 'انضمام'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}